import { useEffect, useState } from 'react';
import { api } from '../api';
import { ErrorState, reviewedApplicationError } from '../ui/State';
import { formatDateOnly, formatDateTime } from '../dates';

type Row = Record<string, any>;
export function OriginalStatementPools({accountId,building,revision,busy,manage,act}: {
  accountId:string;building:string|undefined;revision:number;busy:boolean;manage:boolean;
  act:(path:string,body:Row,message:string)=>Promise<unknown>;
}) {
  const [data,setData]=useState<Row>(),[error,setError]=useState<unknown>(),[page,setPage]=useState(1);
  useEffect(()=>{
    const c=new AbortController();setData(undefined);setError(undefined);setPage(1);
    if(accountId)void api<Row>(`/api/finance/bank-control/accounts/${accountId}/statement-pools${building?'?building='+building:''}`,{signal:c.signal})
      .then(value=>{if(c.signal.aborted)return;if(value.bankAccountId!==accountId)throw reviewedApplicationError('The original statement review did not match the selected bank account.');setData(value)}).catch(e=>{if(!c.signal.aborted)setError(e)});
    return()=>c.abort();
  },[accountId,building,revision]);
  if(!accountId)return null;
  const items=[...(data?.proposals??[]).map((p:Row)=>({...p,proposal:true})),...(data?.reviews??[])];
  if(!items.length&&!error&&!data?.truncated)return null;
  return <section aria-label="Original bank cash group reviews">
    <h3>Original bank cash groups</h3>
    {Boolean(error)&&<ErrorState error={error}/>}
    {data?.truncated&&<p role="alert">More source records need review. A partial set cannot be confirmed as a group.</p>}
    {items.slice((page-1)*10,page*10).map((p:Row)=><article className="panel" key={p.proposal?p.groupId+':'+p.statementId:p.id}>
      <h4>{p.fileName??p.file_name}</h4>
      {p.proposal?<>
        <p>Source lines {p.lineNumbers.join(', ')} · {p.count} existing receipts of GBP {p.each} · {formatDateOnly(p.date)}</p>
        <p>{p.description}</p>
        <p>Confirming links the original statement rows and existing bank records as a group. Individual row pairings remain unproven.</p>
        {manage&&<button disabled={busy} onClick={()=>void act(`/accounts/${accountId}/statement-pools/confirm`,{groupId:p.groupId,statementId:p.statementId,checksum:p.checksum,factsHash:p.factsHash},'Original statement group matched to existing cash. No bank transaction added.')}>Confirm group match</button>}
      </>:<>
        <p>{p.native_ids.length} bank movements · {p.reversal_id?'Reopened':'Group match confirmed'} · {formatDateTime(p.created_at)}</p>
        <p>{p.reason}</p>
        {!p.reversal_id&&manage&&<button disabled={busy||p.reconciliation_status==='Signed off'} onClick={()=>void act(`/statement-pools/${p.id}/reopen`,{},'Group review reopened. Original statement rows await review again.')}>Reopen group review</button>}
      </>}
    </article>)}
    {items.length>10&&<div className="actions"><button disabled={page===1} onClick={()=>setPage(p=>p-1)}>Previous cash groups</button><span>Page {page}</span><button disabled={page*10>=items.length} onClick={()=>setPage(p=>p+1)}>Next cash groups</button></div>}
  </section>;
}
